import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkButton } from "~/common/components/Link";
import { useSimpleMenuState, MenuBottomCornersRounded } from "~/common/components/Menu/Menu";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { MICROSOFT_TEAMS } from "~/utils/url/staticResources";
import { Box } from "~/common/mui";
import ShareIcon from "@mui/icons-material/Share";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";

const COPY_TO_CLIPBOARD = {
  icon: <LinkIcon sx={{ fontSize: 20 }} />,
  text: "share.copy",
};
const COPIED_TO_CLIPBOARD = {
  icon: <CheckIcon sx={{ fontSize: 20 }} />,
  text: "share.copied",
};

const ShareButton = ({
  url,
  title,
  trackOnShare,
  trackOnShareWith,
}: {
  url: string;
  title?: string;
  trackOnShare: () => void;
  trackOnShareWith: (shareWith: string) => void;
}) => {
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();
  const [clipboardMessage, setClipboardMessage] = useState(COPY_TO_CLIPBOARD);
  const isTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const subject = t("share.subject", { product: title });
  const message = `${t("media.library.preview.share.intro", {})}
   ${title}
   ${url}`;
  const messageWithoutUrl = `${t("media.library.preview.share.intro", {})}
   ${title}`;

  const shareToMSTeams = () => {
    trackOnShareWith("MS Teams");
    const URI = `MSTeams:/l/chat/0/0?users=&message=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const copyToClipboard = () => {
    trackOnShareWith("Copy link");
    navigator.clipboard.writeText(url);
    setClipboardMessage(COPIED_TO_CLIPBOARD);
  };

  const divRef = useRef(null);

  const onClick = () => {
    trackOnShare();
    setAnchorEl(divRef.current);
  };

  const mobileShare = async () => {
    try {
      await navigator.share({
        title: subject,
        text: messageWithoutUrl,
        url: url,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (isTablet) {
    return (
      <Box ref={divRef}>
        <LinkButton onClick={onClick}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ShareIcon className={"icon-margin-l scha__header__language--icon"}  />
            <span>{t("page.wishlist.action.share")}</span>
          </Box>
        </LinkButton>

        <MenuBottomCornersRounded
          anchorEl={anchorEl}
          open={isOpen}
          {...MENU_POS_RIGHT}
          onClose={() => {
            handleClose();
            setClipboardMessage(COPY_TO_CLIPBOARD);
          }}
          slotProps={{ list: { disablePadding: true } }}
        >
          <MenuItem sx={{ gap: 1, width: "216px", whiteSpace: "break-spaces" }} onClick={shareToMSTeams}>
            <img src={MICROSOFT_TEAMS} />
            {t("share.msteams")}
          </MenuItem>
          <MenuItem sx={{ gap: 1, width: "216px", whiteSpace: "break-spaces" }} onClick={copyToClipboard}>
            {clipboardMessage.icon}
            {t(clipboardMessage.text)}
          </MenuItem>
        </MenuBottomCornersRounded>
      </Box>
    );
  }

  return (
    <LinkButton onClick={mobileShare}>
      <ShareIcon className={"icon-margin-l scha__header__language--icon"} sx={{ fontSize: 20 }} />
    </LinkButton>
  );
};

export default ShareButton;
